import {
    FetchServiceCardSettingsResponse,
    FetchServicesListingPayload,
    FetchServicesListingResponse,
    ServiceCard,
} from './serviceList.types';
import { updatePaginationButtons } from '../pagination/pagination';
import axios from 'axios';
import saveOrRemoveFavourite from '../../../helpers/favourites';
import copyToClipboard from '../../../helpers/copyToClipboard';
import { IsFavourited } from '../../../helpers/isFavourite';
import IsUserLoggedIn from '../../../helpers/isUserLoggedIn';
import addGoogleAnalyticsEventLog from '../../../helpers/addGoogleAnalyticsEventLog';

const servicesListing = (document.querySelector('.service-list') as HTMLDivElement) || null;

if (servicesListing) {
    const grid = servicesListing.querySelector('.service-list__grid') as HTMLDivElement;
    const filterMobile = servicesListing.querySelector('.service-list__filter-mobile') as HTMLButtonElement;
    const nextPageButton = servicesListing.querySelector('.pagination__page-next') as HTMLButtonElement;
    const prevPageButton = servicesListing.querySelector('.pagination__page-prev') as HTMLButtonElement;
    const paginationDropdown = servicesListing.querySelector('.pagination__dropdown') as HTMLSelectElement;
    const paginationLast = servicesListing.querySelector('.pagination__page-last') as HTMLSelectElement;
    const paginationFirst = servicesListing.querySelector('.pagination__page-first') as HTMLSelectElement;
    const pagination = servicesListing.querySelector('.pagination') as HTMLDivElement;
    const radios: NodeListOf<HTMLInputElement> = servicesListing.querySelectorAll('.sort-filter__input');
    const servicesListingSearchInput = servicesListing.querySelector('.service-list__search-input') as HTMLInputElement;
    const servicesFilters = servicesListing.querySelector('.service-list__filters') as HTMLDivElement;
    const dropdownFilters: NodeListOf<HTMLDivElement> = servicesListing.querySelectorAll('.dropdown-filter');
    const currentCulture: string = document.documentElement.getAttribute('culture');
    const favouritedIds: number[] = JSON.parse(grid.getAttribute('data-ids'));

    let servicesCards: ServiceCard[] = [];
    let keyword = '';
    let educationalLevels = [];
    let serviceTypes = [];
    let beneficiaries = [];
    let sortBy = 1;
    let pageNumber = 1;
    let pageSize = +paginationDropdown.value;
    let totalPages = 0;
    let totalCount: number = 0;
    let hasNextPage = false;
    let hasPreviousPage = false;
    let buttonsToShow: number = 3;
    let isLoading = false;
    let clickedNumber = null;
    let keywordChanged = false;

    const restoreDropdownSelections = (dropdown: HTMLDivElement, selectedValues: string[]) => {
        const select = dropdown?.querySelector('.multi-select') as HTMLSelectElement;
        const buttonContainer = dropdown?.querySelector('.dropdown-filter__button-container') as HTMLDivElement;

        if (!select) return;

        selectedValues.forEach((value) => {
            const option = Array.from(select.options).find((item) => item.value === value);

            if (!option) return;

            option.selected = true;
            option.setAttribute('selected', '');

            if (!buttonContainer) return;

            const alreadyRendered = Array.from(buttonContainer.querySelectorAll('.selected-label')).some(
                (label) => label.textContent === value,
            );

            if (alreadyRendered) return;

            const token = document.createElement('div');
            token.classList.add('dropdown-filter__selected-wrapper');

            const label = document.createElement('span');
            label.classList.add('selected-label');
            label.textContent = value;

            const close = document.createElement('a');
            close.classList.add('selected-close');
            close.setAttribute('tabindex', '-1');
            close.setAttribute('data-option', value);
            close.setAttribute('data-hits', '0');
            close.addEventListener('click', (event) => {
                option.selected = false;
                option.removeAttribute('selected');
                token.remove();
                event.stopPropagation();
            });

            token.appendChild(label);
            token.appendChild(close);
            buttonContainer.appendChild(token);
        });
    };

    const initialUrl = new URL(window.location.href);
    restoreDropdownSelections(dropdownFilters[0], initialUrl.searchParams.getAll('beneficiaries'));
    restoreDropdownSelections(dropdownFilters[1], initialUrl.searchParams.getAll('serviceTypes'));
    restoreDropdownSelections(dropdownFilters[2], initialUrl.searchParams.getAll('educationalLevels'));

    let serviceCardSettings: FetchServiceCardSettingsResponse = {
        cardButtonText: '',
        availableText: '',
        endText: '',
    };

    const fetchServiceCardSettings = async () => {
        try {
            const response = await axios.get<FetchServiceCardSettingsResponse>(
                `/api/services/cardsettings?currentCulture=${currentCulture}`,
            );

            serviceCardSettings = response.data;
        } catch (error) {
            console.error(error);
        }
    };

    const fetchServices = async (page = null) => {
        clickedNumber = page;
        pagination.classList.add('hidden');

        if (clickedNumber) {
            pageNumber = page;
        }

        const url = new URL(window.location.href);

        pageNumber = keywordChanged ? 1 : Number(url.searchParams.get('pageNumber') || 1);
        pageSize = Number(url.searchParams.get('pageSize') || pageSize);
        keyword = keywordChanged ? keyword : url.searchParams.get('keyword') || '';
        educationalLevels =
            educationalLevels.length === 0 ? url.searchParams.getAll('educationalLevels') : educationalLevels;
        serviceTypes = serviceTypes.length === 0 ? url.searchParams.getAll('serviceTypes') : serviceTypes;
        beneficiaries = beneficiaries.length === 0 ? url.searchParams.getAll('beneficiaries') : beneficiaries;
        sortBy = Number(url.searchParams.get('sortBy') || sortBy);

        paginationDropdown.value = pageSize.toString();
        servicesListingSearchInput.value = keyword;
        radios.forEach((radio) => {
            radio.checked = sortBy === +radio.value;
        });

        const payload: FetchServicesListingPayload = {
            keyword,
            educationalLevels,
            serviceTypes,
            beneficiaries,
            sortBy,
            pageNumber,
            pageSize,
            currentCulture,
        };

        isLoading = true;
        disableInputs();

        try {
            // For developement:
            // const response = await axios.get<FetchNewsListingResponse>('https://localhost:44323/api/services', {
            //     params: payload,
            //     headers: {
            //         'Access-Control-Allow-Origin': '*'
            //     }
            // });

            const response = await axios.post<FetchServicesListingResponse>('/api/services', payload);

            servicesCards = response.data.items;

            if (servicesCards.length > 0) {
                pagination.classList.remove('hidden');
            }

            totalCount = response.data.totalCount;
            totalPages = response.data.totalPages;
            hasNextPage = response.data.hasNextPage;
            hasPreviousPage = response.data.hasPreviousPage;
        } catch (error) {
            console.error(error);
            enableInputs();
        } finally {
            updatePaginationButtons(
                totalPages,
                hasPreviousPage,
                hasNextPage,
                pageNumber,
                pageSize,
                totalCount,
                buttonsToShow,
                servicesCards,
                renderPage,
                true
            );
            isLoading = false;
            updateCards();
            enableInputs();
        }
    };

    const disableInputs = () => {
        pagination.classList.add('disabled');
        servicesFilters.classList.add('disabled');
    };

    const enableInputs = () => {
        pagination.classList.remove('disabled');
        servicesFilters.classList.remove('disabled');
    };

    const updateCards = () => {
        grid.innerHTML = '';

        servicesCards.forEach((card: ServiceCard) => {
            const cardElement = document.createElement('a');
            cardElement.className = 'service-list__card fade-in';
            cardElement.setAttribute('href', card.link);
            cardElement.addEventListener('click', (e) => {
                addGoogleAnalyticsEventLog('content_page_view', {page_title: 'Services', content_type: 'Service', language: currentCulture});
            });
            var _class = '';
            if (IsFavourited(card.id, favouritedIds)) {
                _class = 'active';
            }

            cardElement.innerHTML = `
                ${
                    card.isAvailable ? `<p class='service-list__card-tag'>${ serviceCardSettings.availableText}</p>` : `<p class='service-list__card-tag e-services__card-closed'>${ serviceCardSettings.endText}</p>`
                }
				<h3 class='service-list__card-title'>${card.title}</h3>
				<p class='service-list__card-text'>
						${card.description}
				</p>
				<div class='service-list__card-bottom flex flex--justify-between flex--align-center'>
                        <div class="service-list__link flex flex--align-center flex--justify-center">
                            ${serviceCardSettings.cardButtonText}
                            <img src="/assets/img/img/arrow-top-left-dark.svg" alt="arrow icon"
                                class="service-list__link-icon">
                        </div>
					<div class='service-list__card-buttons flex flex--align-center'>
                        ${
                            IsUserLoggedIn()
                                ? `<button class="button button--favourite-small favouriteButton service-list__button flex flex--justify-center flex--align-center ${_class}" data-type="service" data-id="${card.id}">
                                </button>`
                                : ''
                        }
						<button class="button  button--round-small shareButton service-list__button-share service-list__button  flex flex--justify-center flex--align-center">
								<img src="/assets/img/img/share-icon.svg" alt="share icon" class="button__image">
						</button>
					</div>
				</div>
				`;

            grid.appendChild(cardElement);
        });

        addCardListeners();
    };

    // Listeners
    paginationDropdown.addEventListener('change', () => {
        pageSize = +paginationDropdown.value;
        pageNumber = 1;
        renderPage();
    });

    nextPageButton.addEventListener('click', () => {
        pageNumber++;

        renderPage();
    });

    prevPageButton.addEventListener('click', () => {
        pageNumber--;

        renderPage();
    });

    paginationFirst.addEventListener('click', () => {
        pageNumber = 1;
        renderPage();
    });

    paginationLast.addEventListener('click', () => {
        pageNumber = totalPages;
        renderPage();
    });

    let timeout = null;
    servicesListingSearchInput.addEventListener('input', (e) => {
        clearTimeout(timeout);

        timeout = setTimeout(function () {
            keyword = servicesListingSearchInput.value.trim();
            keywordChanged = true;
            pageNumber = 1;
            fetchServices();
        }, 1000);
    });

    radios.forEach((radio) => {
        // 1 = Latest
        // 2 = A-Z
        // 3 = Z-A

        radio.addEventListener('change', () => {
            const checkedRadio = servicesListing.querySelector('input[name="sort-filter"]:checked') as HTMLInputElement;
            const checkedRadioValue = checkedRadio.value;
            sortBy = +checkedRadioValue;
            renderPage();
        });
    });

    // Observe changes in dropdown filters
    dropdownFilters.forEach((dropdown, index) => {
        const optionsContainer = dropdown.querySelector('.dropdown-filter__button-container') as HTMLDivElement | null;

        if (optionsContainer) {
            const config: MutationObserverInit = { childList: true };

            const callback: MutationCallback = (mutationsList: MutationRecord[], observer: MutationObserver) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList') {
                        switch (index) {
                            case 0:
                                const beneficiariesOptions = [];
                                Array.from(optionsContainer.children).forEach((element) => {
                                    beneficiariesOptions.push(element.textContent);
                                });
                                beneficiaries = beneficiariesOptions;
                                break;
                            case 1:
                                const serviceTypeOptions = [];
                                Array.from(optionsContainer.children).forEach((element) => {
                                    serviceTypeOptions.push(element.textContent);
                                });
                                serviceTypes = serviceTypeOptions;
                                break;
                            case 2:
                                const educationalLevelOptions = [];
                                Array.from(optionsContainer.children).forEach((element) => {
                                    educationalLevelOptions.push(element.textContent);
                                });
                                educationalLevels = educationalLevelOptions;
                                break;
                            default:
                                break;
                        }

                        pageNumber = 1;
                        renderPage();
                    }
                }
            };

            const observer: MutationObserver = new MutationObserver(callback);

            observer.observe(optionsContainer, config);
        }
    });

    filterMobile.addEventListener('click', () => {
        const dropdowns = servicesListing.querySelector('.service-list__dropdowns') as HTMLDivElement;
        dropdowns.classList.toggle('service-list__dropdowns--active');
    });

    const initializeEventsList = async () => {
        await fetchServiceCardSettings();
        await fetchServices();
    };

    // Call the initialize function
    initializeEventsList();

    const addCardListeners = () => {
        const serviceListCards: NodeListOf<HTMLAnchorElement> = servicesListing.querySelectorAll('.service-list__card');

        if (serviceListCards.length > 0) {
            const cardButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll('.service-list__button');

            serviceListCards.forEach((card) => {
                card.addEventListener('click', (e) => {
                    const target = e.target as HTMLElement;

                    if (target.classList.contains('button') || target.classList.contains('button__image')) {
                        e.preventDefault();
                    }
                });
            });

            cardButtons.forEach((button) => {
                button.addEventListener('click', (e) => {
                    if (button.classList.contains('favouriteButton')) {
                        let t = button.getAttribute('data-type');
                        var id = Number(button.getAttribute('data-id'));
                        saveOrRemoveFavourite(t, id, button);
                    } else if (button.classList.contains('shareButton')) {
                        copyToClipboard(button);
                    }
                });
            });
        }
    };

    const renderPage = (page = null) => {
        if (page) {
            pageNumber = page;
        }

        const url = new URL(window.location.href.replace(window.location.search, ''));
        url.searchParams.set('pageNumber', pageNumber.toString());
        url.searchParams.set('pageSize', pageSize.toString());
        if (keyword) url.searchParams.set('keyword', keyword);
        educationalLevels.forEach((educationalLevel) =>
            url.searchParams.append('educationalLevels', educationalLevel),
        );
        serviceTypes.forEach((serviceType) => url.searchParams.append('serviceTypes', serviceType));
        beneficiaries.forEach((beneficiary) => url.searchParams.append('beneficiaries', beneficiary));
        url.searchParams.set('sortBy', sortBy.toString());

        if (page == null)
        {
            window.location.href = url.toString();
        }
        return url.toString();
    };
}
